import { Injectable, inject, signal } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { BiometriaService } from '@/core/api/sicom-api/registro/medico/biometria/biometria.service';
import { LoggerService } from '@/core/services/logger.service';
import { TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';

/**
 * Cache de retratos de médico, por `crm|uf`.
 *
 * <p>Vive num serviço, e não no componente, porque a mesma tela costuma pedir o mesmo médico mais de
 * uma vez — na Diretoria são quatro cargos, e nada impede que o mesmo profissional ocupe dois. Sem
 * cache seriam quatro requisições para duas fotos.
 *
 * <p>`null` no cache significa "já perguntei e não há foto", e é diferente de "ainda não perguntei":
 * médico sem retrato é o caso comum, e sem essa distinção a tela rebuscaria a mesma ausência a cada
 * render.
 */
@Injectable({ providedIn: 'root' })
export class FotoMedicoCacheService {
    private readonly biometria = inject(BiometriaService);
    private readonly logger = inject(LoggerService);

    /** Resultados já resolvidos. Exposto como signal para quem quiser reagir sem re-buscar. */
    private readonly fotos = signal<ReadonlyMap<string, string | null>>(new Map());
    /** Buscas em voo, para duas chamadas simultâneas do mesmo médico virarem uma requisição só. */
    private readonly emVoo = new Map<string, Observable<string | null>>();

    chave(crm: number | string | null | undefined, uf: string | null | undefined): string | null {
        const crmTexto = String(crm ?? '').trim();
        const ufTexto = (uf ?? '').trim();
        return crmTexto && ufTexto ? `${crmTexto}|${ufTexto.toUpperCase()}` : null;
    }

    /** Foto já resolvida, se houver. Não dispara busca. */
    emCache(crm: number | string | null | undefined, uf: string | null | undefined): string | null {
        const chave = this.chave(crm, uf);
        return chave ? (this.fotos().get(chave) ?? null) : null;
    }

    /**
     * Valor pronto para uma coluna `type: 'perfil'` do `app-custom-table`.
     *
     * <p>Só lê — não dispara busca. Ler o signal aqui dentro é o que faz a linha se refazer sozinha
     * quando a foto chega: as tabelas montam as linhas em `computed()`, e o `computed` passa a
     * depender do cache. Quem carrega é o {@link precarregar}, chamado uma vez por página de dados.
     *
     * <p>Buscar aqui seria efeito colateral dentro de um `computed`, que reexecuta a cada leitura.
     */
    perfilMedico(nome: string | null | undefined, crm: number | string | null | undefined, uf: string | null | undefined): TablePerfilValue {
        const limpo = (nome ?? '').trim();
        const valido = limpo !== '' && limpo !== '-';
        return {
            nome: valido ? limpo : '',
            foto: valido ? this.emCache(crm, uf) : null,
            iniciais: valido ? this.iniciaisDe(limpo) : null
        };
    }

    /**
     * Dispara a busca das fotos de uma página de dados. Chamada uma vez, quando os dados chegam —
     * chaves repetidas e já resolvidas são ignoradas pelo cache.
     *
     * <p>Devolve um aviso de conclusão para as telas que montam as linhas num array comum, e
     * portanto precisam reescrevê-las quando os retratos chegam. Quem monta em `computed()` sobre
     * o cache pode ignorar o retorno: a linha se refaz sozinha.
     *
     * <p>Assina por conta própria, de propósito. `forkJoin` é frio, e a maior parte das telas só
     * quer o efeito colateral de encher o cache — se a busca dependesse da assinatura de quem
     * chama, ignorar o retorno (o uso previsto) não buscaria retrato nenhum.
     */
    precarregar(pessoas: ReadonlyArray<{ crm: number | string | null | undefined; uf: string | null | undefined }>): Observable<void> {
        const buscas = pessoas.filter((pessoa) => this.chave(pessoa.crm, pessoa.uf)).map((pessoa) => this.buscar(pessoa.crm, pessoa.uf));

        if (!buscas.length) {
            return of(undefined);
        }

        // `refCount: false` para a assinatura de quem chama reaproveitar o resultado em vez de
        // refazer as buscas — e para chegar pronta, quando os retratos já estiverem em cache.
        const conclusao = forkJoin(buscas).pipe(
            map(() => undefined),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        conclusao.subscribe();
        return conclusao;
    }

    /** No máximo duas iniciais — o mesmo recorte dos avatares do resto do sistema. */
    private iniciaisDe(nome: string): string {
        return nome
            .split(/\s+/)
            .slice(0, 2)
            .map((parte) => parte[0])
            .join('')
            .toUpperCase();
    }

    /**
     * Retrato do médico como data URI, ou `null` quando não há.
     *
     * <p>Falha vira `null`, nunca erro na tela: a foto é enfeite, e derrubar o card por causa dela
     * seria trocar um retrato ausente por uma tela quebrada.
     */
    buscar(crm: number | string | null | undefined, uf: string | null | undefined): Observable<string | null> {
        const chave = this.chave(crm, uf);
        if (!chave) {
            return of(null);
        }

        const cacheado = this.fotos();
        if (cacheado.has(chave)) {
            return of(cacheado.get(chave) ?? null);
        }

        const emVoo = this.emVoo.get(chave);
        if (emVoo) {
            return emVoo;
        }

        const requisicao = this.biometria.buscarFotoPorCrmUf(String(crm), String(uf)).pipe(
            map((resposta) => this.paraDataUri(resposta?.imagemBase64)),
            catchError((falha: unknown) => {
                this.logger.error('Falha ao carregar a foto do médico:', falha);
                return of(null);
            }),
            tap((foto) => {
                this.fotos.update((atual) => new Map(atual).set(chave, foto));
                this.emVoo.delete(chave);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        this.emVoo.set(chave, requisicao);
        return requisicao;
    }

    /** O backend às vezes manda o base64 cru, às vezes já com o cabeçalho `data:`. */
    private paraDataUri(base64: string | null | undefined): string | null {
        const conteudo = (base64 ?? '').trim();
        if (!conteudo) {
            return null;
        }
        return conteudo.startsWith('data:') ? conteudo : `data:image/jpeg;base64,${conteudo}`;
    }
}
